import {useMutation} from '@tanstack/react-query';
import {useCallback, useEffect, useState} from 'react';
import isAppWebView from '../../config/isAppWebView';
import {verifyReCaptchaToken} from '../repositories/verifyReCaptchaTokenRepository';

const useClient = (): boolean => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') setIsClient(true);
  }, []);

  return isClient;
};

export const useReCaptcha = () => {
  const isClient = useClient();
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';
  const recaptchaScriptUrl = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
  const [isReady, setIsReady] = useState(false);

  const {mutateAsync: verify, isLoading} = useMutation(
    ['recaptcha/verify'],
    async (token: string) => verifyReCaptchaToken({token}),
  );

  useEffect(() => {
    if (isClient && !isAppWebView()) {
      const head = document.querySelectorAll('head')[0] as HTMLElement;
      const scriptUrl = document.createElement('script');
      scriptUrl.src = recaptchaScriptUrl;
      head.append(scriptUrl);
      const intervalId = setInterval(() => {
        if (typeof grecaptcha !== 'undefined') {
          grecaptcha.enterprise.ready(() => {
            setIsReady(true);
          });
          clearInterval(intervalId);
        }
      }, 1000);
    }
  }, [isClient]);

  const execute = useCallback(
    async (action: string) => {
      if (!isReady) {
        throw new Error('ReCaptcha is not ready');
      }

      return grecaptcha.enterprise.execute(siteKey, {action});
    },
    [isReady, siteKey],
  );

  const reCaptcha = useCallback(
    async (action: string) => {
      if (isAppWebView()) {
        return;
      }

      const token = await execute(action);
      const result = await verify(token);

      if (!result.success) {
        throw new Error('ReCaptcha verification failed');
      }

      return result.success;
    },
    [execute, verify],
  );

  return {
    reCaptcha,
    isLoading,
  };
};
